import { useCallback, useEffect, useState } from "react";

import { type BillingStandaloneOnboardingAWSModel, SaaSConsoleType } from "@doitintl/cmp-models";
import { isRealtimeDatabase } from "@doitintl/models-any";

import { useCustomerId } from "../../../../Components/hooks/useCustomerId";
import { onboardingDocRef } from "../utils";

export function useSaaSConsoleAWSOnboarding(initCompleted?: boolean) {
  const customerId = useCustomerId();
  const [onboardingDoc, setOnboardingDoc] = useState<BillingStandaloneOnboardingAWSModel | undefined>();

  const loadData = useCallback(
    () =>
      onboardingDocRef(customerId, SaaSConsoleType.AWS)
        .narrow<BillingStandaloneOnboardingAWSModel>()
        .get()
        .then((docSnap) => {
          const data = docSnap.data();
          if (data) {
            setOnboardingDoc(data);
          }

          return data;
        }),
    [customerId]
  );

  // fetch the existing data for the customer
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Realtime or polling logic
  useEffect(() => {
    if (!initCompleted) {
      return;
    }

    const ref = onboardingDocRef(customerId, SaaSConsoleType.AWS).narrow<BillingStandaloneOnboardingAWSModel>();

    if (isRealtimeDatabase()) {
      const unsubscribe = ref.onSnapshot((docSnap) => {
        const data = docSnap.data();
        if (data) {
          setOnboardingDoc(data);
        }
      });
      return unsubscribe;
    }

    let cancelled = false;
    const interval = setInterval(() => {
      if (cancelled) {
        return;
      }

      loadData().then((data) => {
        if (data) {
          clearInterval(interval); // Stop polling after successful fetch
        }
      });
    }, 5000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [customerId, initCompleted, loadData]);

  return onboardingDoc;
}
